<template>
    <Head title="Create Award Group"/>
    <BreezeAuthenticatedLayout>
        <template #header>
            <h2 class="font-semibold text-xl leading-tight">
                Update Award Group {{ group.name }}
            </h2>
        </template>
        <BreadCrumbs :items="navItems"></BreadCrumbs>
        <div class="w-full my-6 overflow-hidden py-12 shadow">
            <div class="px-6 pt-4 pb-2">
                <form enctype="multipart/form-data" @submit.prevent="submit" class="w-full">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div class="mb-6 md:mb-4">
                            <admin-app-label>Name</admin-app-label>
                            <input-basic
                                placeholder="Name"
                                v-model="form.name"
                                :error="errors['name']"
                            />
                            <input-error v-if="errors['name']">
                                {{ errors.name }}
                            </input-error>
                        </div>
                        <div class="mb-6 md:mb-4">
                            <admin-app-label>Order</admin-app-label>
                            <input-basic
                                type="number"
                                placeholder="Order"
                                v-model="form.order"
                                :error="errors['order']"
                            />
                        </div>
                    </div>
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div class="mb-6 md:mb-4">
                            <admin-app-label>Year</admin-app-label>
                            <input-select
                                placeholder="Year"
                                label-key="Year"
                                v-model="form.year"
                                :options="years"
                                :error="errors['year']"
                                label="year"/>
                        </div>
                        <div class="mb-6 md:mb-5">
                            <admin-app-label>Season</admin-app-label>
                            <input-select
                                placeholder="Season"
                                label-key="Season"
                                v-model="form.season"
                                :options="seasons"
                                :error="errors['season']"
                                label="season"/>
                        </div>
                        <div class="mb-6 md:mb-5">
                            <admin-app-label>Classification</admin-app-label>
                            <input-select
                                placeholder="Classification"
                                label-key="Classification"
                                v-model="form.application"
                                :options="classifications"
                                :error="errors['application']"
                                label="classification"
                                :reduce="classification => classification.classification"
                            />
                        </div>
                    </div>
                    <div class="mb-6 md:mb-4">
                        <admin-app-label>Related Review</admin-app-label>
                        <input-select
                            placeholder="Steves Review"
                            label-key="title"
                            v-model="form.post_id"
                            :options="posts"
                            :error="errors['post_id']"
                            label="title"
                            :reduce="posts => (posts.id)"
                        />
                    </div>
                    <app-button type="submit" :disabled="form.processing" class="mt-8">Update</app-button>
                </form>
            </div>
        </div>
        <div v-if="group.badges.length" class="relative w-full py-2 my-6 overflow-hidden shadow">
            <div class="px-2 md:px-6 pt-2 md:pt-4 pb-2">
                <h2 class="font-semibold text-xl leading-tight mb-4">
                    Awards For Group: {{ group.name }}
                </h2>
                <div class="overflow-x-auto">
                    <div class="inline-block min-w-full overflow-hidden">
                        <table class="w-full table-auto md:table-fixed">
                            <thead class="uppercase text-gray-600 text-xs font-semibold bg-gray-200">
                            <tr class="hidden lg:table-row">
                                <th class="bg-grey-100 border-b-2 border-grey-200 font-semibold px-5 py-3 uppercase text-sm text-left tracking-wider">ID</th>
                                <th class="bg-grey-100 border-b-2 border-grey-200 font-semibold px-5 py-3 uppercase text-sm text-left tracking-wider">Name</th>
                                <th class="bg-grey-100 border-b-2 border-grey-200 font-semibold px-5 py-3 uppercase text-sm text-left tracking-wider">&nbsp;</th>
                            </tr>
                            </thead>
                            <tbody class="my-3 flex-1 lg:flex-none">
                            <tr v-if="group.badges"
                                v-for="award of group.badges"
                                class="mb-2 lg:mb-0 border-2 lg:border-b-2 border-grey-200 lg:border-t lg:first:border-t-0 flex p-1 md:p-3 hover:bg-gray-100 lg:table-row flex-col w-full flex-wrap">
                                <td class="p-1 lg:p-5 lg:border-l-2 lg:border-b border-grey-200 bg-white text-sm md:text-base">
                                    <label class="mb-3 bg-grey-100 border-b-2 border-grey-200 font-semibold px-5 py-3 uppercase text-sm text-left tracking-wider block lg:hidden">ID</label>
                                    {{ award.id }}
                                </td>
                                <td class="p-1 lg:p-5 lg:border-b border-grey-200 bg-white text-sm md:text-base">
                                    <label class="mb-3 bg-grey-100 border-b-2 border-grey-200 font-semibold px-5 py-3 uppercase text-sm text-left tracking-wider block lg:hidden">Title</label>
                                    {{ award.name }}
                                </td>
                                <td class="px-5 py-5 lg:border-r lg:border-b border-grey-200 bg-white text-center lg:text-left text-sm md:text-base">
                                    <admin-link :href="route('admin.awards.edit', award)">Edit</admin-link>
                                </td>
                            </tr>
                            <tr v-else>
                                <td class="py-6 bg-red" colspan="3" align="center">
                                    <p class="text-white font-bold">No awards found</p>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </BreezeAuthenticatedLayout>
</template>

<script>
import {useForm} from "@inertiajs/inertia-vue3";
import AdminLink from "@/Components/Admin/AdminLink";
import AppButton from "@/Components/AppButton";
import AdminAppLabel from "@/Components/Admin/Forms/AdminAppLabel";
import BreezeAuthenticatedLayout from "@/Layouts/Admin/Authenticated";
import BreadCrumbs from "@/Components/Admin/BreadCrumbs";
import InputBasic from "@/Components/Admin/Forms/Inputs/InputBasic";
import InputError from "@/Components/Forms/Inputs/InputError";
import InputSelect from "@/Components/Admin/Forms/Inputs/InputSelect";

export default {
    name: "Edit",
    components: {
        AdminAppLabel,
        AdminLink,
        AppButton,
        BreadCrumbs,
        BreezeAuthenticatedLayout,
        InputBasic,
        InputSelect,
        InputError
    },
    props: {
        group: Object,
        seasons: Object,
        classifications: Object,
        posts: Object,
        years: Object,
        errors: Object
    },
    data() {
        return {
            navItems: [
                {title: "Award Groups", path: "/admin/award-groups"},
            ],
            form: this.$inertia.form({
                ...this.group,
                _method: "put",
            })
        }
    },
    mounted() {
        this.navItems.push({
            title: this.group.name,
            path: this.route("admin.award-groups.edit", this.group)
        });
    },
    methods: {
        submit()
        {
            this.form.post(`/admin/award-groups/${this.group.id}`);
        }
    }
}
</script>

<style scoped>

</style>
